import { Box, CircularProgress } from "@mui/material";
import { getErrorMessage } from "@src/appV2/api/errors";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { Text } from "@src/lib/ui-react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { type ExploJwtRequestType, useExploToken } from "../api/useExploToken";
import { EXPLO_EVENTS, EXPLO_ORIGIN } from "../constants";

interface ExploEmbedProps {
  workplaceId: string;
  embedId: string;
  tmz?: string;
}

export function ExploEmbed({ workplaceId, embedId, tmz }: ExploEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const exploJwtRequest = useMemo(
    (): ExploJwtRequestType => ({
      data: {
        attributes: {
          workplaceId,
          embedId,
        },
      },
    }),
    [workplaceId, embedId]
  );

  const {
    mutateAsync: fetchExploJwt,
    isLoading: isFetchingToken,
    isError,
    error,
  } = useExploToken({
    onSuccess: (data) => {
      if (iframeRef.current) {
        const url = new URL(`${EXPLO_ORIGIN}/iframe/${data.data.attributes.jwt}`);
        if (tmz) {
          url.searchParams.set("timezone", tmz);
        }

        iframeRef.current.src = url.toString();
      }
    },
  });

  const isLoading = isFetchingToken && !iframeRef.current?.src;

  const handleMessage = useCallback(
    ({ origin, data }: MessageEvent) => {
      if (origin !== EXPLO_ORIGIN) {
        return;
      }

      // eslint-disable-next-line default-case
      switch (data.event) {
        case EXPLO_EVENTS.CUSTOMER_JWT_EXPIRED: {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          fetchExploJwt(exploJwtRequest).then((response) => {
            iframeRef.current?.contentWindow?.postMessage(
              {
                event: EXPLO_EVENTS.UPDATE_CUSTOMER_JWT,
                detail: { jwt: response.data.attributes.jwt },
              },
              EXPLO_ORIGIN
            );
          });
          break;
        }

        case EXPLO_EVENTS.DASHBOARD_FAILED:
        case EXPLO_EVENTS.ERROR: {
          logError(APP_V2_APP_EVENTS.EXPLO_IFRAME_ERROR, {
            error: data.detail?.error ?? "Unknown error",
            metadata: {
              ...exploJwtRequest.data.attributes,
              errorType: data.event,
            },
          });
          break;
        }
      }
    },
    [fetchExploJwt, exploJwtRequest]
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchExploJwt(exploJwtRequest);

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage, fetchExploJwt, exploJwtRequest]);

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Text variant="h2">
          {getErrorMessage(error, "Failed to load dashboard. Please try again.")}
        </Text>
      )}

      {!isError && (
        <Box
          ref={iframeRef}
          component="iframe"
          id="explo-iframe"
          title={`Explo Dashboard ${embedId}`}
          sx={{
            border: "none",
            height: "100%",
            width: "100%",
            display: isLoading ? "none" : "block",
          }}
          allow="clipboard-write"
        />
      )}
    </Box>
  );
}

export default ExploEmbed;
